/***
 *
 *   TICKET CONVERSATION
 *   Comments and activity, inline in the drawer's main column — this is what
 *   people open a ticket for, so it is no longer behind a tab.
 *
 *   A sticky All / Comments / Activity filter narrows the feed, entries are
 *   grouped by day, and events get a small icon node instead of an avatar so
 *   comments read as the substance and events as the trail between them.
 *
 *   The scroll container and the composer live in details.js, so the feed can
 *   share one scroll with the description and attachments above it.
 *
 **********/

import React, { useContext, useMemo, useState } from 'react';
import * as dayjs from 'dayjs';
import Style from './history.tailwind';
import { AuthContext } from 'app/auth';
import { useTranslate } from 'app/translations';
import { CommentsHistory } from '../history/comments/comments';
import { EventsHistory } from '../history/events/events';
import { Icon, UserAvatar, cn, initialsFromName } from 'components/lib';
import { displayName } from 'utilities/names';

/* Feather names keyed by the event family — action_type.split('_')[0], the same
   key events.js resolves its component with. */
const EVENT_ICONS = {
    created: 'plus-circle',
    status: 'activity',
    priority: 'flag',
    assignee: 'user-plus',
    due: 'calendar',
    name: 'type',
    description: 'align-left',
    attachment: 'paperclip',
    section: 'arrow-right',
    sync: 'refresh-cw',
    tag: 'tag',
    time: 'clock',
    moved: 'arrow-right',
};

/* Only the handful worth spotting while scanning get colour. */
const EVENT_TONES = {
    created:
        'border-accent-300/60 bg-accent-50 text-accent-600 dark:border-accent-800 dark:bg-accent-900/30 dark:text-accent-200',
    status: 'border-primary-300/60 bg-primary-50 text-primary-600 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-200',
    deleted: 'border-error/40 bg-error/10 text-error',
};

export function isComment(history) {
    return (
        history?.action_type === 'comment_posted' || history?.action_type === 'comment_updated'
    );
}

function groupByDay(entries, labels) {
    const groups = [];

    entries.forEach((entry) => {
        const day = dayjs(entry.date_created);
        const key = day.format('YYYY-MM-DD');
        const last = groups[groups.length - 1];

        if (last && last.key === key) {
            last.entries.push(entry);
            return;
        }

        // `LL` needs dayjs's localizedFormat plugin, which this app does not
        // load — the native formatter is already how due dates render here.
        const label = day.isSame(dayjs(), 'day')
            ? labels.today
            : day.isSame(dayjs().subtract(1, 'day'), 'day')
              ? labels.yesterday
              : day.toDate().toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                });

        groups.push({ key, label, entries: [entry] });
    });

    return groups;
}

function DayDivider({ label }) {
    return (
        <div className={Style.divider} aria-hidden>
            <span className={Style.dividerRule} />
            <span className={Style.dividerLabel}>{label}</span>
            <span className={Style.dividerRule} />
        </div>
    );
}

function FilterTabs({ value, onChange, counts }) {
    const { _t } = useTranslate();

    const tabs = [
        { key: 'all', label: _t('ticket.detail.filterAll'), count: counts.all },
        { key: 'comments', label: _t('ticket.detail.filterComments'), count: counts.comments },
        { key: 'activity', label: _t('ticket.detail.filterActivity'), count: counts.activity },
    ];

    return (
        <div className={Style.tabs} role="tablist" aria-label={_t('ticket.detail.conversation')}>
            {tabs.map((tab) => {
                const active = value === tab.key;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(tab.key)}
                        className={cn(Style.tab, active ? Style.tabActive : Style.tabIdle)}>
                        {tab.label}
                        <span className={cn(Style.tabCount, active && Style.tabCountActive)}>
                            {tab.count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function TicketHistory(props) {
    const { _t } = useTranslate();
    const { ticket } = props;
    const { user } = useContext(AuthContext);
    const { histories } = ticket;
    const [filter, setFilter] = useState('all');

    const counts = useMemo(() => {
        const all = histories?.length || 0;
        const comments = histories?.filter(isComment).length || 0;
        return { all, comments, activity: all - comments };
    }, [histories]);

    const visible = useMemo(() => {
        if (!histories?.length) return [];
        if (filter === 'comments') return histories.filter(isComment);
        if (filter === 'activity') return histories.filter((h) => !isComment(h));
        return histories;
    }, [histories, filter]);

    const groups = groupByDay(visible, {
        today: _t('ticket.detail.today'),
        yesterday: _t('ticket.detail.yesterday'),
    });

    const emptyMessage =
        filter === 'comments'
            ? _t('ticket.detail.noComments')
            : filter === 'activity'
              ? _t('ticket.detail.noActivityOnly')
              : _t('ticket.detail.noActivity');

    return (
        <section className={Style.wrapper}>
            <div className={Style.stickyBar}>
                <h3 className={Style.barTitle}>
                    <Icon
                        image="message-square"
                        size={15}
                        color="currentColor"
                        className="text-text-muted"
                    />
                    {_t('ticket.detail.conversation')}
                </h3>
                <FilterTabs value={filter} onChange={setFilter} counts={counts} />
            </div>

            <div className={Style.feed}>
                {visible.length ? (
                    groups.map((group) => (
                        <div key={group.key} className={Style.group}>
                            <DayDivider label={group.label} />
                            <ul className={Style.list}>
                                {group.entries.map((history, index) => {
                                    const comment = isComment(history);
                                    const HistoryComponent = comment
                                        ? CommentsHistory
                                        : EventsHistory;
                                    const family = history.action_type?.split('_')[0];
                                    const name = displayName(history.creator_name);
                                    const isLast = index === group.entries.length - 1;

                                    return (
                                        <li
                                            key={history.id || index}
                                            className={cn(
                                                Style.item,
                                                isLast
                                                    ? 'pb-0'
                                                    : comment
                                                      ? 'pb-4'
                                                      : 'pb-3',
                                            )}>
                                            {!isLast && (
                                                <span className={Style.connector} aria-hidden />
                                            )}

                                            <span className={Style.node}>
                                                {comment ? (
                                                    <UserAvatar
                                                        name={name}
                                                        initials={initialsFromName(name)}
                                                        size="md"
                                                        className="ring-2 ring-background"
                                                        fallbackClassName="text-[11px]"
                                                    />
                                                ) : (
                                                    <span
                                                        className={cn(
                                                            Style.eventNode,
                                                            EVENT_TONES[family],
                                                        )}>
                                                        <Icon
                                                            image={
                                                                EVENT_ICONS[family] || 'edit-3'
                                                            }
                                                            size={13}
                                                            color="currentColor"
                                                        />
                                                    </span>
                                                )}
                                            </span>

                                            <HistoryComponent
                                                history={history}
                                                ticket={ticket}
                                                user={user}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))
                ) : (
                    <div className={Style.empty}>
                        <span className={Style.emptyIcon}>
                            <Icon
                                image={filter === 'activity' ? 'clock' : 'message-square'}
                                size={20}
                                color="currentColor"
                                className="text-text-muted"
                            />
                        </span>
                        <p className={Style.emptyText}>{emptyMessage}</p>
                    </div>
                )}
            </div>
        </section>
    );
}

export default TicketHistory;
